import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Button, Spinner, Title } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { currentUserAtom, userFetchErrorAtom } from "@/store/userAtoms";
import { AccountLoadError } from "@components/Auth/AccountLoadError";
import {
    ApprovalStatus,
    MembershipStatus,
    isCustomerWalled,
} from "@models/user";
import { OrganizationStatus } from "@models/organization";
import { PaymentReturned } from "./_components/PaymentReturned";
import { PaymentMethodActionForRole } from "./_components/PaymentMethodActionForRole";

// Payment-required wall (#321, DecisionLog §1.2). Reached when a signed-in,
// APPROVED user's org membership is not Active (awaiting a first payment
// method / lapsed / suspended / cancelled / no active location). Distinct from
// the /c/pending application wall.
//
// The AwaitingPaymentMethod branch carries the card surface INLINE (#375) for
// the organization's Admin: that customer was just approved, has never had a
// card, and capturing one is what activates the membership (§1.6). The lapsed
// branch (PaymentRequired) and the declined-renewal branch (a Suspended
// subscription) send the organization's Admin to the membership tab, which
// since #376 carries the same panel in its "update" variant. On all three,
// anyone else is told to ask their administrator, since only the Admin may save
// a card (#368, PaymentMethodActionForRole). A suspension for a returned bank
// payment (#445) gets the PaymentReturned view, which carries the "update"
// panel inline for the organization's Admin. The organization on hold,
// cancelled membership, cancelled organization and no-active-location branches
// offer no card at all (#368): a card fixes none of them. What a save did to
// the membership (activated, renewed via #381, or nothing beyond storing the
// card) comes back in the response's `outcome`, and the panel claims exactly
// that and no more (MB-07).
export const PaymentRequiredPage = () => {
    const user = useAtomValue(currentUserAtom);
    const hasFetchError = useAtomValue(userFetchErrorAtom);

    // Loading window — currentUserAtom is null until getMeUser() resolves.
    // A failed read is its own state, not a spinner that never ends.
    if (!user) {
        if (hasFetchError) {
            return <AccountLoadError />;
        }

        return (
            <div className="flex justify-center py-20">
                <Spinner size="lg" />
            </div>
        );
    }

    // A not-yet-approved user belongs on the application wall, not here.
    if (user.approvalStatus !== ApprovalStatus.Approved) {
        return <Navigate to={ROUTES.PENDING} replace />;
    }

    // Not a wall state — Active, NoLocationMembership (#410), absent, null or
    // a value this build does not know — means the app, not this page: the
    // same predicate ApprovalGate applies (isCustomerWalled; approval is
    // settled above), so the two can never disagree and bounce a shopper
    // between the wall and the gate.
    if (!isCustomerWalled(user)) {
        return <Navigate to={ROUTES.BROWSE} replace />;
    }

    // #445: absent on an API before PR 379, read as none.
    const returnedPaymentOrders = user.returnedPaymentOrders ?? [];

    // #368: the organization's own standing behind a Suspended or Cancelled
    // membership. An OrganizationStatus, never a MembershipStatus: the two
    // share values (membership Cancelled is 4, the organization's Suspended).
    const organizationStatus = user.organizationStatus;

    let content: ReactNode;
    if (
        user.membershipStatus === MembershipStatus.Cancelled &&
        organizationStatus === OrganizationStatus.Cancelled
    ) {
        // The organization itself is closed (cancelled, or soft-deleted): not a
        // subscription that ended, so not the membership copy below.
        // TODO: Cancelled-org copy/behavior is provisional pending a business decision (#321 follow-up).
        content = (
            <>
                <Title>Account Cancelled</Title>
                <p className="text-base mt-6">
                    Your organization's account has been cancelled. Contact
                    support if you have any questions.
                </p>
                <div className="mt-8">
                    <Button
                        to={ROUTES.SUPPORT}
                        color={ComponentColor.Primary}
                        className="normal-case"
                    >
                        Contact Support
                    </Button>
                </div>
            </>
        );
    } else if (user.membershipStatus === MembershipStatus.Cancelled) {
        // Also the cancelled subscription of an organization that is on hold
        // (organizationStatus Suspended): the membership really is cancelled,
        // which is what this copy says.
        content = (
            <>
                <Title>Membership Cancelled</Title>
                <p className="text-base mt-6">
                    Your membership has been cancelled. Contact support to
                    discuss reactivating your account.
                </p>
                <div className="mt-8">
                    <Button
                        to={ROUTES.SUPPORT}
                        color={ComponentColor.Primary}
                        className="normal-case"
                    >
                        Contact Support
                    </Button>
                </div>
            </>
        );
    } else if (
        user.membershipStatus === MembershipStatus.Suspended &&
        returnedPaymentOrders.length > 0
    ) {
        // A returned bank payment (#445 / audit C-11) is the one suspension
        // with a page of its own. Checked after Cancelled, matching the API's
        // precedence: a cancelled membership keeps the Cancelled page.
        content = (
            <PaymentReturned
                userId={user.id}
                orders={returnedPaymentOrders}
                hasCardSinceReturn={user.hasCardSinceReturn ?? false}
            />
        );
    } else if (
        user.membershipStatus === MembershipStatus.Suspended &&
        organizationStatus === OrganizationStatus.Suspended
    ) {
        // The organization itself is on hold, put there by an admin (a hold
        // for a returned payment took the branch above). Only an admin lifts
        // it, so no Update Payment Method: a card cannot fix this (PO ruling
        // 2026-11-03).
        content = (
            <>
                <Title>Account on Hold</Title>
                <p className="text-base mt-6">
                    Your organization's account is on hold. Contact support to
                    resolve it.
                </p>
                <div className="mt-8">
                    <Button
                        to={ROUTES.SUPPORT}
                        color={ComponentColor.Primary}
                        className="normal-case"
                    >
                        Contact Support
                    </Button>
                </div>
            </>
        );
    } else if (user.membershipStatus === MembershipStatus.Suspended) {
        // Only the subscription is suspended. The API suspends a subscription
        // only when a renewal charge is declined (by the card issuer, or in the
        // payment provider's fraud review), and /users/me still reports the
        // membership Active while the paid period runs, so this customer's
        // renewal was declined and their period has ended. Saving a card on
        // the membership tab charges the outstanding renewal at once (#381;
        // not when a cancellation is scheduled or an earlier charge is still
        // unconfirmed), and a charge that goes through makes the membership
        // Active again, so Update Payment Method is the primary action, for
        // the organization's Admin, the one role that may save a card
        // (PaymentMethodActionForRole). (An API before PR 470 sends no
        // organizationStatus, so there an organization on hold lands here
        // too.)
        content = (
            <>
                <Title>Membership Renewal Declined</Title>
                <PaymentMethodActionForRole
                    userId={user.id}
                    lead="Your membership renewal payment was declined, so shopping is paused."
                    adminNext="Update your payment method to renew your membership."
                    tail="Shopping resumes as soon as the renewal payment goes through."
                />
            </>
        );
    } else if (user.membershipStatus === MembershipStatus.NoActiveLocation) {
        // Deliberately says nothing about money or about an application being
        // reviewed: the org is still approved, it just has no active location.
        content = (
            <>
                <Title>No Active Location</Title>
                <p className="text-base mt-6">
                    Your organization has no active approved location, so
                    shopping is paused. Add a new location or restore an
                    existing one before ordering can resume.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                    <Button
                        to={ROUTES.ACCOUNT_LOCATIONS}
                        color={ComponentColor.Primary}
                        className="normal-case"
                    >
                        Manage Locations
                    </Button>
                    <Button
                        to={ROUTES.SUPPORT}
                        color={ComponentColor.Primary}
                        outline
                        className="normal-case"
                    >
                        Contact Support
                    </Button>
                </div>
            </>
        );
    } else if (
        user.membershipStatus === MembershipStatus.AwaitingPaymentMethod
    ) {
        // First-time setup, NOT a lapse: this customer was approved moments
        // ago, has never had a card, and nothing has failed. The copy must not
        // imply a failed payment, a lapse or anything overdue.
        //
        // Still no membership-tab link here on purpose: the panel is already
        // inline on this page, so a link to the tab's copy of it is a detour,
        // not a way out. The panel is the organization's Admin's only; anyone
        // else is told to ask them to add a card (#368).
        content = (
            <>
                <Title>Add a Payment Method</Title>
                <PaymentMethodActionForRole
                    userId={user.id}
                    lead="Your account is approved. Adding a payment method activates your membership and opens up shopping right away."
                    adminAction="addInline"
                />
            </>
        );
    } else {
        // PaymentRequired — the only wall state left once the four above are
        // taken (None and unknown values were redirected to browse above).
        // Update Payment Method only for the organization's Admin, as above.
        content = (
            <>
                <Title>Membership Payment Required</Title>
                <PaymentMethodActionForRole
                    userId={user.id}
                    lead="Your monthly membership payment could not be processed and your membership has lapsed. Shopping access is paused until your payment is brought current."
                />
            </>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-10 text-primary">
            {content}
        </div>
    );
};
